/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppSettings } from 'settings/AppSettings';
import { Crop } from 'react-image-crop';
import { Network } from 'components/OAuth';
import { ServerError, ServerParseError } from '@apollo/client';
import { capitalizeName } from '@tock/shared/utils/patron';
import { core } from '@tock/coretypes';
import { logger } from '@tock/logging';
import { noop, toUpper } from 'es-toolkit/compat';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { AccountCreation, AuthRequest, EmailChangeRequest, ResetTokenRequest } = protos;

interface EmailAuthenticationMethods {
  type: 'email';
  email: string;
}

export function setSettings(settings: AppSettings): BaseTockAction {
  return {
    type: AppConstants.SET_SETTINGS,
    payload: settings,
  };
}

export function runningInReactNative(): BaseTockAction {
  return {
    type: AppConstants.ENABLE_REACT_NATIVE_MODE,
  };
}

export function fetchConfig(domain?: string, cache?: boolean): ApiAction {
  let getProto = '/consumer/config';
  if (domain) {
    getProto = `/consumer/config?domain=${domain}`;
  }

  // added extra check to allow for testing more easily
  if (BROWSER && (window as any).__ENV__ && (window as any).__ENV__.RESTAURANT) {
    getProto = `/consumer/config?domain=${(window as any).__ENV__.RESTAURANT}`;
  }

  return {
    TockApi: {
      getProto,
      type: AppConstants.CONFIG_FETCH,
      context: domain,
      cacheOptions: cache
        ? {
            enableCache: true,
            maxAge: 60 * 1000,
          }
        : undefined,
    },
  };
}

export function login(
  email: string,
  password: string,
  type: 'CONSUMER_UI' | 'PARTNER_UI' = 'CONSUMER_UI',
  chaseToken?: string
): ApiAction {
  const authProvider = chaseToken ? 'CHASE' : 'PASSWORD';
  const request = AuthRequest.fromObject({
    email,
    password,
    accessToken: chaseToken,
    type,
    authProvider,
  });
  return {
    TockApi: {
      postProto: '/authenticate',
      request,
      type: AppConstants.AUTHENTICATION_RESPONSE,
      onError: noop, // Let caller handle errors
    },
  };
}

export function loginWith(network: Network | 'CHASE', accessToken: string): ApiAction {
  const request = AuthRequest.fromObject({
    authProvider: toUpper(network),
    accessToken,
  });
  return {
    TockApi: {
      postProto: '/authenticate',
      request,
      type: AppConstants.AUTHENTICATION_RESPONSE,
      onError: noop, // Let caller handle errors
    },
  };
}

export function fetchRoles(): ApiAction {
  return {
    TockApi: {
      getProto: '/authenticate/roles?authType=PARTNER_UI',
      type: AppConstants.AUTHENTICATION_FETCH_ROLES,
      onError: noop, // Let caller handle errors
    },
  };
}

export function getAuthenticationMethods(
  authenticationMethod: EmailAuthenticationMethods
): ApiAction {
  const queryParam = `email=${encodeURIComponent(authenticationMethod.email)}`;

  return {
    TockApi: {
      getListProto: `/authenticate/methods?${queryParam}`,
      type: AppConstants.AUTHENTICATION_METHODS,
      onError: { 9000: '', defaultToOriginal: true },
    },
  };
}

export function logout(): BaseTockAction {
  return {
    type: AppConstants.AUTHENTICATION_CLEARED_DONE,
  };
}

export function detectLocation(): ApiAction {
  return {
    TockApi: {
      getProto: `/consumer/location/detect`,
      type: AppConstants.DETECT_LOCATION,
      onError: noop, // Let caller handle errors
    },
  };
}

type ErrorMessageType = 'TOAST' | 'MANUAL';

type ResetPasswordOpts = {
  showToast?: boolean;
};

export function resetPassword(
  email: string,
  errorMessageType: ErrorMessageType = 'TOAST',
  { showToast = true }: ResetPasswordOpts = {}
): ApiAction {
  const action: ApiAction = {
    TockApi: {
      getProto: `/password/reset?email=${encodeURIComponent(email)}`,
      type: AppConstants.NULL,
      toast: showToast
        ? `We've sent password reset instructions to ${email}. Check your inbox.`
        : null,
    },
  };

  if (errorMessageType === 'MANUAL') {
    action.TockApi.onError = noop;
  }
  return action;
}

export function resetPasswordWithToken(email: string, password: string, token: string): ApiAction {
  const request = ResetTokenRequest.fromObject({
    email,
    password,
    token,
  });
  return {
    TockApi: {
      postProto: '/password/reset/token',
      request,
      type: AppConstants.NULL,
      toast: 'Your password has been reset. You can now log in with your new password.',
    },
  };
}

export function createAccount(email: string, password: string): ApiAction {
  const request = AccountCreation.fromObject({
    email,
    password,
    authProvider: 'PASSWORD',
  });
  return {
    TockApi: {
      postProto: '/patron',
      request,
      type: AppConstants.CREATE_ACCOUNT,
      onSuccess: trackAccountCreation,
    },
  };
}

// We know that certain of these fields won't be null and won't be undefined - this type construct allows us to
// express that. Simply using Required<> would eliminate undefined but not null.
//
// Oddly, if we call from FinishAccountSetup we are not required to provide phone, first name, or last name.
export type CreateAccountWithProfileRequest = {
  [P in 'email' | 'password']: NonNullable<core.IAccountCreation[P]>;
} & core.IAccountCreation;

export function createAccountWithProfile(
  request: CreateAccountWithProfileRequest,
  requireChallengeResponse: boolean
): ApiAction {
  const { challengeResponse } = request;

  if (!challengeResponse && requireChallengeResponse) {
    logger.error(
      `The challengeResponse field being passed into createAccountWithProfile() is ${challengeResponse}; but based on
      the calling environment, form validation, and feature flags challengeResponse is required. It will likely fail.`
    );
  }

  return {
    TockApi: {
      postProto: '/patron',
      request: AccountCreation.fromObject(request),
      type: AppConstants.CREATE_ACCOUNT,
      onError: {
        2010: '', // Account already exists
        defaultToOriginal: true,
      },
      onSuccess: trackAccountCreation,
    },
  };
}

export function createAccountIfAuthenticated(request: core.IAccountCreation): ApiAction {
  return {
    TockApi: {
      postProto: '/patron/signup-token',
      request: AccountCreation.fromObject(request),
      type: AppConstants.CREATE_ACCOUNT,
      onSuccess: trackAccountCreation,
    },
  };
}

export function createAccountWith(
  network: Network,
  accessToken: string,
  patronData: Partial<core.AccountCreation>
): ApiAction {
  const request = AccountCreation.fromObject({
    authProvider: toUpper(network),
    accessToken,
    ...patronData,
  });
  return {
    TockApi: {
      postProto: '/patron',
      request,
      type: AppConstants.CREATE_ACCOUNT,
      onError: {
        2010: '',
        default: `Unable to sign up via ${network}`,
      },
      onSuccess: trackAccountCreation,
    },
  };
}

function trackAccountCreation() {
  window.analytics.fbqTrack('track', 'CompleteRegistration');
}

export function fetchPatronProfile(): ApiAction<core.PatronProfile> {
  return {
    TockApi: {
      getProto: '/patron/profile',
      type: AppConstants.PATRON_PROFILE_FETCH_PROFILE,
    },
  };
}

export function activateAccount(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/activate?token=${token}`,
      type: AppConstants.NULL,
      toast: 'Your account has been activated.',
    },
  };
}

export function patronActivateResend(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron/activate/resend',
      type: AppConstants.NULL,
      toast: 'Another activation email has been sent to your inbox',
    },
  };
}

export function resendVerificationEmail(email: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/send-verification?email=${encodeURIComponent(email)}`,
      type: AppConstants.NULL,
      toast: 'Another activation email has been sent to your inbox',
    },
  };
}

export function updateProfile(profile: core.Patron, toast?: string): ApiAction {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  profile.firstName = capitalizeName(profile.firstName)!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  profile.lastName = capitalizeName(profile.lastName)!;
  return {
    TockApi: {
      putProto: '/patron',
      request: profile,
      type: AppConstants.PATRON_SET_PROFILE,
      toast,
    },
  };
}

export function updatePatronProfile(
  profile: core.PatronProfile,
  showToast = true
): ApiAction<core.PatronProfile> {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  profile.patron!.firstName = capitalizeName(profile.patron!.firstName!)!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  profile.patron!.lastName = capitalizeName(profile.patron!.lastName!)!;
  return {
    TockApi: {
      putProto: '/patron/profile',
      request: profile,
      type: AppConstants.PATRON_PROFILE_SET_PROFILE,
      toast: showToast ? 'Profile updated' : '',
    },
  };
}

export function updatePatronProfileImage(image: any, orientation?: number, crop?: Crop): ApiAction {
  let path = '/patron/profile/image';

  if (crop) {
    const { x, y, width, height } = crop;

    path += `?x=${x}&y=${y}&width=${width && !isNaN(width) ? width : 0}&height=${
      height && !isNaN(height) ? height : 0
    }`;
  }
  if (orientation) {
    if (!crop) {
      path += `?orientation=${orientation}`;
    } else {
      path += `&orientation=${orientation}`;
    }
  }
  return {
    TockApi: {
      postFile: path,
      request: image,
      type: AppConstants.PATRON_PROFILE_SET_IMAGE,
    },
  };
}

export function loginWithAccountActivationToken(token: string): ApiAction {
  const request = AuthRequest.fromObject({
    accessToken: token,
    type: 'CONSUMER_UI',
  });
  return {
    TockApi: {
      postProto: `/patron/activate/auth`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
      request,
    },
  };
}

export function loginWithPurchaseVerifyToken(token: string, isConcierge = false): ApiAction {
  return {
    TockApi: {
      getProto: `/purchase/verify/auth?token=${token}&isConcierge=${isConcierge}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function loginWithTransferToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/transfer/token/auth?token=${token}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function loginWithTransferPurchaseToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/transfer/purchase/token/auth?purchaseToken=${token}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function changeEmail(email: string): ApiAction {
  const request = EmailChangeRequest.fromObject({
    nextEmail: email,
  });
  return {
    TockApi: {
      postProto: '/patron/initiate/email/change',
      request,
      type: AppConstants.PATRON_INITIATE_EMAIL_CHANGE,
      toast: 'Check your inbox for an activation link.',
    },
  };
}

export function confirmChangeEmail(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/email/change?token=${token}`,
      type: AppConstants.PATRON_EMAIL_CHANGE,
      toast: 'Your email has been successfully updated',
    },
  };
}

export function getActiveEmailChange(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron/active/email/change',
      type: AppConstants.PATRON_ACTIVE_EMAIL_CHANGE,
    },
  };
}

export function cancelEmailChange(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/email/change/cancel?token=${token}`,
      type: AppConstants.PATRON_EMAIL_CHANGE_CANCEL,
      toast: 'Request to change email canceled',
    },
  };
}

export function getPatronAuthTypes(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron/auth/types',
      type: AppConstants.PATRON_AUTH_TYPE_GET,
    },
  };
}

export function loginWithUpdateEmailToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/email/change/auth?token=${token}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function cancelAccount(reason: { surveyResponse: string }): ApiAction {
  const deleteReason = reason.surveyResponse || 'NO_RESPONSE';
  return {
    TockApi: {
      deleteProto: `/patron?deleteReason=${deleteReason}`,
      type: AppConstants.ACCOUNT_DELETE,
    },
  };
}

export function setBusinessScope(businessId: number): BaseTockAction {
  return {
    type: AppConstants.SET_BUSINESS_SCOPE,
    payload: businessId,
  };
}

export function changeActivePartner(partner: core.Partner): BaseTockAction {
  return {
    type: AppConstants.CHANGE_ACTIVE_PARTNER_ROLE,
    payload: partner,
  };
}

export function setGoogleMapsLoaded(loaded: boolean): BaseTockAction {
  return {
    type: AppConstants.SET_GOOGLE_MAPS_LOADED,
    payload: loaded,
  };
}

export function setGoogleMapsLoadError(error: Error): BaseTockAction {
  return {
    type: AppConstants.SET_GOOGLE_MAPS_LOAD_ERROR,
    payload: error,
  };
}

export function graphqlError(error: Error | ServerError | ServerParseError) {
  return {
    type: AppConstants.GRAPHQL_ERROR_OCCURED,
    payload: error,
  };
}

export function updateShippingState(shippingState: string) {
  return {
    type: AppConstants.UPDATE_SHIPPING_STATE,
    payload: shippingState,
  };
}
